import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, TextField, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Tooltip, InputAdornment,
} from '@mui/material';
import { FixedSizeList } from 'react-window';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import LabelIcon from '@mui/icons-material/Label';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import axios from 'axios';

import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444', dangerDark: '#dc2626',
};

// ── Column widths (flex fractions map to percentages) ─────────────────────────
// Using percentage-based flex so columns fill the full container width
const COLS = [
  { label: '#',             flex: '0 0 44px',   align: 'left'  },
  { label: 'Code',          flex: '0 0 90px',   align: 'left'  },
  { label: 'Name',          flex: '1 1 180px',  align: 'left'  },  // grows
  { label: 'HSN',           flex: '0 0 100px',  align: 'left'  },
  { label: 'GST %',         flex: '0 0 70px',   align: 'right' },
  { label: 'MRP',           flex: '0 0 80px',   align: 'right' },
  { label: 'Sale Rate',     flex: '0 0 90px',   align: 'right' },
  { label: 'Wholesale',     flex: '0 0 90px',   align: 'right' },
  { label: 'Purchase Rate', flex: '0 0 110px',  align: 'right' },
  { label: '',              flex: '0 0 80px',   align: 'right' },
];

const ROW_HEIGHT  = 36;
const LIST_HEIGHT = 500;

// ── Shared row/cell styles ────────────────────────────────────────────────────
const rowBase = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
  borderBottom: `1px solid ${C.cardBorder}`,
};

function ColCell({ col, children, muted, bold }) {
  return (
    <div style={{
      flex: col.flex,
      minWidth: 0,           // allow text-overflow to work inside flex
      padding: '0 10px',
      fontSize: '12px',
      color: muted ? C.muted : C.text,
      fontWeight: bold ? 600 : 400,
      textAlign: col.align,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      boxSizing: 'border-box',
    }}>
      {children}
    </div>
  );
}

// ── Memoised row ──────────────────────────────────────────────────────────────
const ItemRow = React.memo(({ index, style, data: listData }) => {
  const { items, onDelete, hasEdit, hasDelete } = listData;
  const item = items[index];
  const isEven = index % 2 === 0;

  return (
    <div
      style={{
        ...style,                          // react-window positions this (top, height, width)
        ...rowBase,
        backgroundColor: isEven ? C.card : '#f7fdfc',
        transition: 'background-color 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.accentDim; }}
      onMouseLeave={e => { e.currentTarget.style.backgroundColor = isEven ? C.card : '#f7fdfc'; }}
    >
      <ColCell col={COLS[0]} muted>{index + 1}</ColCell>
      <ColCell col={COLS[1]} bold>{item.Code}</ColCell>
      <ColCell col={COLS[2]}>{item.Name}</ColCell>
      <ColCell col={COLS[3]}>{item.HSNCode}</ColCell>
      <ColCell col={COLS[4]}>{Number(item.CGSTRate) + Number(item.SGSTRate)}%</ColCell>
      <ColCell col={COLS[5]}>{item.MRP}</ColCell>
      <ColCell col={COLS[6]}>{item.SaleRate}</ColCell>
      <ColCell col={COLS[7]}>{item.WholeSaleRate}</ColCell>
      <ColCell col={COLS[8]}>{item.PurchaseRate}</ColCell>
      <ColCell col={COLS[9]}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {hasEdit && (
            <Tooltip title="Edit" arrow>
              <IconButton size="small" component={Link} to={`/itemEdit/${item.Id}`}
                sx={{ color: '#7c3aed', p: '3px', '&:hover': { bgcolor: 'rgba(124,58,237,0.08)' } }}>
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {hasDelete && (
            <Tooltip title="Delete" arrow>
              <IconButton size="small" onClick={() => onDelete(item.Id)}
                sx={{ color: C.danger, p: '3px', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}>
                <DeleteIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </ColCell>
    </div>
  );
});

// ── Main component ────────────────────────────────────────────────────────────
function ItemIndex() {
  const history = useHistory();
  const { isAuthenticated, companyId, branchId, userId, permissions, token, logout } = useAuth();

  const hasCreateMasterPermission = permissions.includes('Create Master');
  const hasEditMasterPermission   = permissions.includes('Edit Master');
  const hasDeleteMasterPermission = permissions.includes('Delete Master');

  const [data, setData]                     = useState([]);
  const [loading, setLoading]               = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // container width for the virtual list
  const containerRef = useRef(null);
  const [listWidth, setListWidth] = useState(800);

  // Delete dialog
  const [deleteId, setDeleteId]     = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [fileData, setFileData]     = useState([]);
  const [importing, setImporting]   = useState(false);

  // Measure container so FixedSizeList fills it exactly
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        setListWidth(entry.contentRect.width);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Debounce search
  const debounceRef = useRef(null);
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(val), 200);
  };

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchTableData();
  }, [isAuthenticated, companyId, branchId, token]);

  const fetchTableData = async () => {
    setLoading(true);
    try {
      const res = await fetchData('ItemApi', `/${companyId}`, token);
      if (res.responseCode === '1111') setData(res.response_data);
    } catch (err) {
      console.error('Error fetching items:', err);
      if (err.response?.status === 401) {
        logout();
        history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Memoised filter
  const filteredData = useMemo(() => {
    if (!debouncedQuery.trim()) return data;
    const q = debouncedQuery.toLowerCase();
    return data.filter(item =>
      item.Code?.toLowerCase().includes(q) ||
      item.Name?.toLowerCase().includes(q)
    );
  }, [data, debouncedQuery]);

  // Stable itemData object passed to FixedSizeList — avoids re-creating rows
  const itemData = useMemo(() => ({
    items: filteredData,
    onDelete: (id) => { setDeleteId(id); setDeleteOpen(true); },
    hasEdit: hasEditMasterPermission,
    hasDelete: hasDeleteMasterPermission,
  }), [filteredData, hasEditMasterPermission, hasDeleteMasterPermission]);

  // Delete
  const closeDelete = useCallback(() => { setDeleteId(null); setDeleteOpen(false); }, []);
  const handleDelete = async () => {
    try {
      await deleteData('ItemApi', `${deleteId}`, token);
      closeDelete();
      fetchTableData();
    } catch (err) {
      console.error('Error deleting item:', err);
    }
  };

  // Import
  const openImport  = () => { setFileData([]); setImportOpen(true); };
  const closeImport = () => { setFileData([]); setImportOpen(false); };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }).slice(1);
      setFileData(
        rows.filter(r => r[2]).map(r => ({
          Material:    r[0]  ? String(r[0])  : '',
          Description: r[1]  ? String(r[1])  : '',
          PG:          r[2]  ? String(r[2])  : '',
          IG:          r[3]  ? String(r[3])  : '',
          ISG:         String(r[4]),
          PF:          r[5]  ? String(r[5])  : '',
          Group:       r[6]  ? String(r[6])  : '',
          HSN:         r[8]  ? String(r[8])  : '',
          MIKCode:     r[9]  ? String(r[9])  : '',
          GST:         r[12] ?? 0,
          MRP:         r[7]  ?? 0,
          Category:    r[11] ?? 0,
        }))
      );
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      await axios.post(
        'https://mga-api.off-ease.com/api/ItemImportApi',
        { CompanyId: companyId, BranchId: branchId, CreatedBy: userId, ItemImport: fileData },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      closeImport();
      fetchTableData();
    } catch (err) {
      console.error('Error importing items:', err);
    } finally {
      setImporting(false);
    }
  };

  // Button styles
  const btnPrimary = {
    fontSize: '11px', fontWeight: 700, textTransform: 'none',
    bgcolor: C.accent, color: '#fff', borderRadius: '7px', px: 1.6, py: 0.5,
    '&:hover': { bgcolor: C.accentDark }, '&:disabled': { bgcolor: C.accentBorder },
  };
  const btnSecondary = {
    fontSize: '11px', fontWeight: 600, textTransform: 'none',
    color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
    borderRadius: '7px', px: 1.4, py: 0.5,
    '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
  };
  const btnDanger = {
    fontSize: '11px', fontWeight: 700, textTransform: 'none',
    bgcolor: C.danger, color: '#fff', borderRadius: '7px', px: 1.6, py: 0.5,
    '&:hover': { bgcolor: C.dangerDark },
  };

  return (
    <Box sx={{ bgcolor: C.bg, p: '6px 6px 6px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <InventoryIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
            Items
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small" placeholder="Search by code or name…"
            value={searchQuery} onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 14, color: C.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 210,
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px', fontSize: '12px', height: '28px', bgcolor: C.card,
                '& fieldset': { borderColor: C.cardBorder },
                '&:hover fieldset': { borderColor: C.accentBorder },
                '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
              },
            }}
          />
          <Tooltip title="Import from Excel" arrow>
            <Button onClick={openImport} size="small"
              startIcon={<UploadFileIcon sx={{ fontSize: 13 }} />}
              sx={{
                fontSize: '11px', fontWeight: 600, textTransform: 'none',
                color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
                borderRadius: '7px', px: 1.2, py: 0.4, boxShadow: C.shadow,
                '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
              }}>
              Import
            </Button>
          </Tooltip>
          {hasCreateMasterPermission && (
            <Button component={Link} to="/ItemCreate" size="small"
              startIcon={<AddIcon sx={{ fontSize: 13 }} />}
              sx={{
                fontSize: '11px', fontWeight: 700, textTransform: 'none',
                bgcolor: C.accent, color: '#fff', borderRadius: '7px',
                px: 1.4, py: 0.4, boxShadow: C.shadow,
                '&:hover': { bgcolor: C.accentDark },
              }}>
              Create
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Table card ── */}
      <Box sx={{
        bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow,
        border: `1px solid ${C.cardBorder}`, overflow: 'hidden',
      }}>

        {/* Card title bar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.8,
          px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`,
          bgcolor: `${C.accent}0d`,
        }}>
          <LabelIcon sx={{ fontSize: 15, color: C.accent }} />
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            Item List
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Typography sx={{ fontSize: '10px', color: C.muted }}>
              {filteredData.length} of {data.length} record{data.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: C.accent }} />
          </Box>
        ) : (
          /* ref container — measured so FixedSizeList gets exact pixel width */
          <div ref={containerRef} style={{ width: '100%' }}>

            {/* ── Column header row ── */}
            <div style={{
              ...rowBase,
              backgroundColor: '#f8fafa',
              borderBottom: `2px solid ${C.cardBorder}`,
              width: listWidth || '100%',
            }}>
              {COLS.map((col, i) => (
                <div key={i} style={{
                  flex: col.flex,
                  minWidth: 0,
                  padding: '7px 10px',
                  fontSize: '10px',
                  fontWeight: 700,
                  color: C.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: col.align,
                  boxSizing: 'border-box',
                }}>
                  {col.label}
                </div>
              ))}
            </div>

            {/* ── Virtualised body ── */}
            {filteredData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5, color: C.muted, fontSize: '12px' }}>
                No records found
              </Box>
            ) : (
              <FixedSizeList
                height={Math.min(LIST_HEIGHT, filteredData.length * ROW_HEIGHT)}
                itemCount={filteredData.length}
                itemSize={ROW_HEIGHT}
                width={listWidth || '100%'}
                itemData={itemData}
                overscanCount={8}
              >
                {ItemRow}
              </FixedSizeList>
            )}
          </div>
        )}
      </Box>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{ fontSize: '13px', fontWeight: 800, color: C.text, borderBottom: `1px solid ${C.cardBorder}`, pb: 0.5 }}>
          Delete Item?
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <DialogContentText sx={{ fontSize: '12px', color: C.muted }}>
            Are you sure you want to delete this item? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 0.8 }}>
          <Button onClick={closeDelete} sx={btnSecondary}>Cancel</Button>
          <Button onClick={handleDelete} sx={btnDanger} startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Import Dialog ── */}
      <Dialog open={importOpen} onClose={closeImport} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '13px', fontWeight: 800, color: C.text, pb: 0.5,
          borderBottom: `1px solid ${C.cardBorder}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <UploadFileIcon sx={{ fontSize: 15, color: C.accent }} />
            Import Items from Excel
          </Box>
          <IconButton size="small" onClick={closeImport} sx={{ color: C.muted }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '14px !important' }}>
          <DialogContentText sx={{ fontSize: '12px', color: C.muted, mb: 1.5 }}>
            Upload an Excel file to import items. Ensure the file format matches the template.
          </DialogContentText>
          <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ fontSize: '12px' }} />
          {fileData.length > 0 && (
            <Typography sx={{ mt: 1, fontSize: '11px', color: '#22c55e', fontWeight: 600 }}>
              {fileData.length} row{fileData.length !== 1 ? 's' : ''} ready to import
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 0.8 }}>
          <Button onClick={closeImport} sx={btnSecondary}>Cancel</Button>
          <Button onClick={handleImport} disabled={importing || fileData.length === 0} sx={btnPrimary}
            startIcon={importing ? <CircularProgress size={11} sx={{ color: '#fff' }} /> : <UploadFileIcon sx={{ fontSize: 13 }} />}>
            {importing ? 'Importing…' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default ItemIndex;

